import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChartLine, FaLightbulb, FaCogs, FaUsers, FaGlobe, FaShieldAlt, FaCheck, FaArrowRight } from 'react-icons/fa';
import { getCollection } from '../firebase/firestore';
import './Services.css';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const iconMap = {
    'chart': <FaChartLine />,
    'lightbulb': <FaLightbulb />,
    'cogs': <FaCogs />,
    'users': <FaUsers />,
    'globe': <FaGlobe />,
    'shield': <FaShieldAlt />
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { db } = await import('../firebase/config');

      if (!db) {
        setServices([]);
        setLoading(false);
        return;
      }

      const firestoreData = await getCollection('services');

      // Filter only active services and add icons
      const activeServices = firestoreData
        .filter(service => service.active)
        .sort((a, b) => a.order - b.order)
        .map(service => ({
          ...service,
          slug: service.slug || service.id,
          icon: iconMap[service.icon] || <FaChartLine />,
          image: service.image || ''
        }));

      setServices(activeServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="services-page">
      <div className="page-header">
        <div className="container" data-aos="fade-up">
          <h1 className="services-section-label">Services</h1>
          <h4 className="services-main-title">Where Triology Partners With Organizations</h4><br />
          <div className="services-header-content">
            <p className="services-subtitle">Consulting that begins with understanding, not assumptions</p>

            <div className="services-intro-grid">
              <div className="services-intro-main">
                <p>
                  Triology’s services are designed for organizations navigating growth, complexity, and transition.
                  Rather than offering predefined packages, we engage where leadership intent, systems, and execution are misaligned.
                </p>
              </div>

              <div className="services-highlight-box">
                <div className="highlight-line"></div>
                <p>
                  Every engagement is <strong>diagnosis-led, context-specific, and execution-governed</strong>,
                  ensuring that solutions are practical and sustainable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="services-page-grid">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', gridColumn: '1/-1' }}>
                <p>Loading services...</p>
              </div>
            ) : services.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', gridColumn: '1/-1' }}>
                <p>No services available.</p>
              </div>
            ) : services.map((service, index) => (
              <Link
                to={`/services/${service.slug}`}
                key={index}
                className="service-page-card-link"
              >
                <div
                  className="service-page-card"
                  data-aos="fade-up"
                  data-aos-duration="800"
                  data-aos-delay={index * 100}
                >
                  {service.image ? (
                    <div className="service-page-image">
                      <img src={service.image} alt={service.title} />
                    </div>
                  ) : (
                    <div className="service-page-icon">{service.icon}</div>
                  )}
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <div className="card-footer-action">
                    <span className="show-more-btn">Show More <FaArrowRight /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="engagement-clarity-section">
        <div className="container">
          <div className="engagement-dual-grid">
            {/* Left Column: HOW WE ENGAGE */}
            <div className="engagement-col" data-aos="fade-right">
              <span className="services-section-label--small">HOW WE ENGAGE</span>
              <h2 className="engagement-title">Flexible partnerships with <br />execution discipline</h2>
              <div className="engagement-text">
                <p>
                  Triology works through a range of engagement models, including diagnostic-led engagements,
                  transformation partnerships, project-based execution support, and capability-building programs.
                </p>
                <p>
                  The structure and pace of each engagement are tailored to the organization’s maturity and leadership readiness.
                </p>
                <div className="engagement-highlight">
                  <p>Across all models, execution is governed through <strong>COSMOS</strong>, ensuring clarity, ownership, and progress tracking.</p>
                </div>
              </div>
            </div>

            {/* Right Column: AN IMPORTANT CLARITY */}
            <div className="engagement-col" data-aos="fade-left">
              <span className="services-section-label--small">AN IMPORTANT CLARITY</span>
              <h2 className="engagement-title">What we deliberately <br />do not offer</h2>
              <div className="clarity-content">
                <p>Triology does not believe in:</p>
                <ul className="clarity-list">
                  <li>one-off interventions without follow-through</li>
                  <li>generic frameworks applied without context</li>
                  <li>advisory reports that are disconnected from execution</li>
                </ul>
                <div className="clarity-footer">
                  <p>Our focus is on <strong>real, sustained change.</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="closing-clarity-section">
        <div className="container" data-aos="zoom-in">
          <div className="closing-card">
            <h2>Every engagement begins with clarity</h2>
            <p>
              If your organization is navigating growth, transition, or increasing complexity,
              Triology helps bring clarity to systems, alignment to leadership, and discipline to execution.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
